import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Link, useParams } from 'react-router-dom';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import LoadingSpinner from './LoadingSpinner';
import { reservationService } from './clients/ApiClients';
import { ReservationUtilizationReport } from './clients/rust-api-client';
import { formatMoney } from './utils/billingHelpers';
import {
  WEEKDAYS_SHORT,
  formatDayLabel,
  formatMinute,
  reservationErrorMessage,
  todayIn,
  tzLabel,
} from './reservation/reservationMeta';
import {
  RANGE_LABELS,
  RangePreset,
  bookingsHeld,
  busyHours,
  formatHours,
  formatPercent,
  gridMax,
  heatLevel,
  presetRange,
  sumGrids,
  totalsOf,
} from './reservation/utilizationMeta';
import { inputClass, labelClass } from './reservation/formStyles';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { FieldPicker } from './common/Custom/FieldPicker';

/**
 * How the association's amenities are used (P2, #642): per amenity, the
 * bookings, booked against open hours, no-shows, early and late
 * cancellations, and fee revenue net of reversals — then when, as a
 * weekday × hour heatmap. Read-only; the board sees it too.
 *
 * No chart library: the heatmap is a CSS grid shaded in five steps.
 */
const HEAT = [
  'bg-fg/[0.03]',
  'bg-accent/20',
  'bg-accent/40',
  'bg-accent/60',
  'bg-accent/85',
];

const ReservationUtilizationPage: React.FC = () => {
  const { hoaId } = useParams<{ hoaId: string }>();
  const [preset, setPreset] = useState<RangePreset | 'custom'>('last30');
  const [range, setRange] = useState(() => presetRange('last30', todayIn(null)));
  const [report, setReport] = useState<ReservationUtilizationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [heatAmenity, setHeatAmenity] = useState<string>('all');

  useEffect(() => {
    if (!hoaId || !range.from || !range.to || range.from > range.to) return;
    let live = true;
    setLoading(true);
    setError(null);
    reservationService
      .getReservationUtilization({ hoaId, from: range.from, to: range.to })
      .then((r) => live && setReport(r.data))
      .catch((err) => live && setError(reservationErrorMessage(err, "Couldn't load the report.")))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [hoaId, range]);

  // Presets are relative to today in the association's zone once known.
  const zone = report?.timezone ?? null;
  const choosePreset = (p: RangePreset | 'custom') => {
    setPreset(p);
    if (p !== 'custom') setRange(presetRange(p, todayIn(zone)));
  };

  const rows = useMemo(
    () => (report?.amenities ?? []).filter((a) => a.booking_mode !== 'info_only'),
    [report]
  );
  const totals = totalsOf(rows);
  const grid = useMemo(() => {
    const picked = heatAmenity === 'all' ? rows : rows.filter((a) => a.amenity_id === heatAmenity);
    return sumGrids(picked.map((a) => a.hours_by_weekday_hour));
  }, [rows, heatAmenity]);
  const max = gridMax(grid);
  const hours = busyHours(grid);
  const hourList = Array.from({ length: hours.end - hours.start }, (_, i) => hours.start + i);
  const thisYear = new Date().getFullYear();

  return (
    <SidebarLayout sidebar={<AppSidebar currentPage="reservations" />} navbar={<Navbar>{/* Your navbar content */}</Navbar>}>
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link to={`/hoa/${hoaId}/reservations`} className="shrink-0 text-fg-muted hover:text-fg">
          Reservations
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        <span className="min-w-0 truncate font-medium text-fg">Utilization</span>
      </div>

      <div className="space-y-6">
        <div className="flex flex-wrap items-end gap-3">
          <h1 className="mr-auto text-2xl/8 font-semibold tracking-tight text-fg">Amenity utilization</h1>
          <div className={labelClass}>
            Range
            <FieldPicker
              size="sm"
              className="w-40"
              label="Range"
              value={preset}
              onChange={(v) => choosePreset(v as RangePreset | 'custom')}
              options={[
                ...(Object.keys(RANGE_LABELS) as RangePreset[]).map((p) => ({ value: p, label: RANGE_LABELS[p] })),
                { value: 'custom', label: 'Custom…' },
              ]}
            />
          </div>
          {preset === 'custom' && (
            <>
              <label className={labelClass}>
                From
                <input
                  type="date"
                  value={range.from}
                  onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                  className={inputClass}
                />
              </label>
              <label className={labelClass}>
                To
                <input
                  type="date"
                  value={range.to}
                  onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                  className={inputClass}
                />
              </label>
            </>
          )}
        </div>
        <p className="text-[12px] text-fg-muted">
          {formatDayLabel(range.from, thisYear)} – {formatDayLabel(range.to, thisYear)} · {tzLabel(zone)}
        </p>

        <ErrorAlert>{error}</ErrorAlert>
        {loading && !report ? (
          <LoadingSpinner />
        ) : rows.length === 0 ? (
          <p className="text-[13px] text-fg-muted">No bookable amenities in this association.</p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-line">
              <table className="w-full text-[13px]">
                <thead className="border-b border-line text-left text-[12px] text-fg-muted">
                  <tr>
                    <th className="px-3 py-2 font-medium">Amenity</th>
                    <th className="px-3 py-2 text-right font-medium">Bookings</th>
                    <th className="px-3 py-2 text-right font-medium">Booked</th>
                    <th className="px-3 py-2 text-right font-medium">Open</th>
                    <th className="px-3 py-2 text-right font-medium">Utilization</th>
                    <th className="px-3 py-2 text-right font-medium">No-shows</th>
                    <th className="px-3 py-2 text-right font-medium" title="Cancelled before / after the free-cancellation deadline">
                      Cancelled (early / late)
                    </th>
                    <th className="px-3 py-2 text-right font-medium">Fee revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line/50 tabular-nums">
                  {rows.map((a) => (
                    <tr key={a.amenity_id}>
                      <td className="px-3 py-2">
                        <Link to={`/hoa/${hoaId}/amenities/${a.amenity_id}`} className="text-fg hover:underline">
                          {a.name}
                        </Link>
                        {a.bookings.pending > 0 && (
                          <span className="ml-1.5 text-[12px] text-fg-muted">· {a.bookings.pending} pending</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">{bookingsHeld(a)}</td>
                      <td className="px-3 py-2 text-right">{formatHours(a.booked_hours)}</td>
                      <td className="px-3 py-2 text-right text-fg-muted">{formatHours(a.open_hours)}</td>
                      <td className="px-3 py-2 text-right">
                        <UtilizationBar percent={a.utilization_percent} />
                      </td>
                      <td className={clsx('px-3 py-2 text-right', a.no_shows > 0 && 'text-warning')}>{a.no_shows}</td>
                      <td className="px-3 py-2 text-right">
                        {a.cancellations_early} / {a.cancellations_late}
                      </td>
                      <td className="px-3 py-2 text-right">{formatMoney(a.fee_revenue)}</td>
                    </tr>
                  ))}
                </tbody>
                {rows.length > 1 && (
                  <tfoot className="border-t border-line font-medium tabular-nums">
                    <tr>
                      <td className="px-3 py-2">All amenities</td>
                      <td className="px-3 py-2 text-right">{totals.bookings}</td>
                      <td className="px-3 py-2 text-right">{formatHours(totals.bookedHours)}</td>
                      <td className="px-3 py-2 text-right text-fg-muted">{formatHours(totals.openHours)}</td>
                      <td className="px-3 py-2 text-right">
                        {formatPercent(totals.openHours > 0 ? (totals.bookedHours / totals.openHours) * 100 : null)}
                      </td>
                      <td className="px-3 py-2 text-right">{totals.noShows}</td>
                      <td className="px-3 py-2 text-right">
                        {totals.cancellationsEarly} / {totals.cancellationsLate}
                      </td>
                      <td className="px-3 py-2 text-right">{formatMoney(totals.feeRevenue)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <p className="text-[12px] text-fg-muted">
              Bookings are approved and completed ones. Fee revenue is net of reversals, late-cancellation fees
              included. Overnight stays count their whole stay as booked time.
            </p>

            <section aria-labelledby="heatmap-title" className="space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                <h2 id="heatmap-title" className="mr-auto text-[15px] font-semibold text-fg">
                  When it is busy
                </h2>
                <div className={labelClass}>
                  Amenity
                  <FieldPicker
                    size="sm"
                    className="w-48"
                    label="Amenity"
                    value={heatAmenity}
                    onChange={setHeatAmenity}
                    options={[
                      { value: 'all', label: 'All amenities' },
                      ...rows.map((a) => ({ value: a.amenity_id, label: a.name })),
                    ]}
                  />
                </div>
              </div>
              {max === 0 ? (
                <p className="text-[13px] text-fg-muted">Nothing booked in this range.</p>
              ) : (
                <div className="overflow-x-auto">
                  <div
                    role="table"
                    aria-label="Booked hours by weekday and hour"
                    className="inline-grid gap-px text-[10px] text-fg-muted"
                    style={{ gridTemplateColumns: `3rem repeat(${hourList.length}, minmax(1.75rem, 1fr))` }}
                  >
                    <span role="columnheader" />
                    {hourList.map((h) => (
                      <span key={h} role="columnheader" className="pb-1 text-center">
                        {h % 3 === 0 ? formatMinute(h * 60).replace(':00', '') : ''}
                      </span>
                    ))}
                    {WEEKDAYS_SHORT.map((day, d) => (
                      <React.Fragment key={day}>
                        <span role="rowheader" className="pr-2 text-right leading-6">
                          {day}
                        </span>
                        {hourList.map((h) => {
                          const v = grid[d][h];
                          return (
                            <span
                              key={h}
                              role="cell"
                              className={clsx('h-6 rounded-sm', HEAT[heatLevel(v, max)])}
                              title={`${day} ${formatMinute(h * 60)}: ${formatHours(Math.round(v * 10) / 10)} booked`}
                            />
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                  <p className="mt-2 flex items-center gap-1.5 text-[11px] text-fg-muted">
                    Less
                    {HEAT.map((c, i) => (
                      <span key={c} className={clsx('h-3 w-3 rounded-sm', c)} aria-hidden title={`Level ${i}`} />
                    ))}
                    More · busiest hour {formatHours(Math.round(max * 10) / 10)} booked
                  </p>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </SidebarLayout>
  );
};

const UtilizationBar: React.FC<{ percent: number | null | undefined }> = ({ percent }) => (
  <span className="inline-flex items-center justify-end gap-2">
    {percent !== null && percent !== undefined && (
      <span className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-fg/10 sm:inline-block" aria-hidden>
        <span className="block h-full rounded-full bg-accent" style={{ width: `${Math.min(100, percent)}%` }} />
      </span>
    )}
    {formatPercent(percent)}
  </span>
);

export default ReservationUtilizationPage;
